/*
  Manual Registrations: data layer.

  DEMO STORE. The screens talk to the real API (manualRegistrationsRealApi.js) unless
  REACT_APP_MANUAL_REGISTRATIONS_DEMO=true is set, or the tests run. This in-memory store (reset on page
  refresh) behaves like that API: same field names, the same paginated list shape, "duplicate reference"
  and "already submitted" errors, and the same subscription rules. Use it to try the screens without a backend.

  REST contract (all under /api/product-admin, Product Admin token), implemented by the backend:
    GET  /manual-registrations/plans
    GET  /manual-registrations/companies?search=         company search with current subscription state
    GET  /manual-registrations?page&page_size&search&status&payment_method&change_type    -> { items, total }
    GET  /manual-registrations/stats
    POST /manual-registrations                           multipart; Idempotency-Key header
    GET  /manual-registrations/:id
    GET  /manual-registrations/:id/proof                 authenticated receipt download
    POST /manual-registrations/:id/cancel                { reason }
    GET  /companies/:id/subscription-history

  Errors mimic axios (err.response.status / err.response.data.error). Only `manualRegistrationsApi` is used
  by the screens; `describeChange` and `subscriptionState` are shared helpers.
*/
import { CHANGE_TYPES, priceBreakdown } from './manualRegistrationUtils';
import { realApi } from './manualRegistrationsRealApi';

const DAY = 86400000;
const DEMO_DELAY_MS = 250;
const DOCUMENTS_PER_LICENCE = 10000;   // matches the app's default documents per licence
const ADMIN_NAME = 'Product Admin';

const delay = (ms = DEMO_DELAY_MS) => new Promise((resolve) => setTimeout(resolve, ms));
const iso = (daysFromNow) => new Date(Date.now() + daysFromNow * DAY).toISOString();
const clone = (value) => JSON.parse(JSON.stringify(value));

function failure(status, message) {
  const error = new Error(message);
  error.response = { status, data: { error: message } };
  return error;
}

// Demo plans: same tiers as the app's pricing plans. Prices are per licence, before GST.
const PLANS = [
  { id: 2, name: 'Basic', price_per_licence: 2200, duration_days: 365, min_licences: 1, max_licences: 4 },
  { id: 3, name: 'Pro', price_per_licence: 1800, duration_days: 365, min_licences: 5, max_licences: 10 },
  { id: 4, name: 'Business', price_per_licence: 1500, duration_days: 365, min_licences: 11, max_licences: 50 },
  { id: 5, name: 'Enterprise', price_per_licence: 1000, duration_days: 365, min_licences: 51, max_licences: 500 },
];

// ---- demo companies; `history` is newest first, history[0] is the current subscription ----
const entry = (id, fields) => ({
  id, kind: 'razorpay', change_type: 'new', previous_plan_name: null, state: 'active', close_reason: null,
  documents_used: 0, document_limit: null, licences: 1, amount: 0, currency: 'INR', method: null,
  reference: null, registration_id: null, has_receipt: false, actor: null, ...fields,
});

const companies = [
  {
    id: 101, name: 'Millennium Infotech', tokens: 1,
    admins: [{ id: 101, name: 'Rahul Sharma', email: 'rahul@millennium.example', phone: '9876500101' }],
    history: [entry('h101a', { kind: 'trial', plan_name: 'Free Trial', start: iso(-18), expiry: iso(12), documents_used: 7, document_limit: 30, created_at: iso(-18) })],
  },
  {
    id: 102, name: 'DbgCo', tokens: 1,
    admins: [
      { id: 102, name: 'Sneha Rao', email: 'sneha@dbgco.example', phone: '9876500102' },
      { id: 112, name: 'Kiran Das', email: 'kiran@dbgco.example', phone: '9876500112' },
    ],
    history: [entry('h102a', { kind: 'trial', plan_name: 'Free Trial', start: iso(-60), expiry: iso(-30), state: 'expired', documents_used: 30, document_limit: 30, created_at: iso(-60) })],
  },
  {
    id: 103, name: 'E2E Co Updated', tokens: 10,
    admins: [{ id: 103, name: 'Anil Kumar', email: 'anil@e2eco.example', phone: '9876500103' }],
    history: [
      entry('h103b', { kind: 'razorpay', change_type: 'trial_conversion', plan_name: 'Pro', previous_plan_name: 'Free Trial', start: iso(-300), expiry: iso(65), licences: 10, document_limit: 100000, documents_used: 18420, amount: 21240, method: 'razorpay', reference: 'pay_QX81mYk2', actor: 'Customer (online)', created_at: iso(-300) }),
      entry('h103a', { kind: 'trial', plan_name: 'Free Trial', start: iso(-330), expiry: iso(-300), state: 'closed', close_reason: 'Converted to paid', documents_used: 22, document_limit: 30, created_at: iso(-330) }),
    ],
  },
  {
    id: 104, name: 'Acme Traders', tokens: 3,
    admins: [{ id: 104, name: 'Priya Nair', email: 'priya@acmetraders.example', phone: '9876500104' }],
    history: [
      entry('h104b', { kind: 'razorpay', change_type: 'new', plan_name: 'Basic', start: iso(-400), expiry: iso(-35), state: 'expired', licences: 3, document_limit: 30000, documents_used: 12950, amount: 7788, method: 'razorpay', reference: 'pay_PQ44aZ91', actor: 'Customer (online)', created_at: iso(-400) }),
    ],
  },
  {
    id: 105, name: 'MITI-TEST', tokens: 1,
    admins: [{ id: 105, name: 'Test Admin', email: 'test@miti.example', phone: '9876500105' }],
    history: [entry('h105a', { kind: 'trial', plan_name: 'Free Trial', start: iso(-90), expiry: iso(-60), state: 'expired', documents_used: 11, document_limit: 30, created_at: iso(-90) })],
  },
  {
    id: 106, name: 'Zenith Labs', tokens: 6,
    admins: [{ id: 106, name: 'Meera Joshi', email: 'meera@zenithlabs.example', phone: '9876500106' }],
    history: [
      entry('h106a', { kind: 'razorpay', change_type: 'new', plan_name: 'Pro', start: iso(-165), expiry: iso(200), licences: 6, document_limit: 60000, documents_used: 940, amount: 12744, method: 'razorpay', reference: 'pay_RT55wLq9', actor: 'Customer (online)', created_at: iso(-165) }),
    ],
  },
  {
    id: 107, name: 'Orbit Solutions', tokens: 2,
    admins: [{ id: 107, name: 'Vikram Singh', email: 'vikram@orbitsolutions.example', phone: '9876500107' }],
    history: [
      entry('h107a', { kind: 'razorpay', change_type: 'new', plan_name: 'Basic', start: iso(-420), expiry: iso(-55), state: 'expired', licences: 2, document_limit: 20000, documents_used: 6110, amount: 5192, method: 'razorpay', reference: 'pay_NB20cEd7', actor: 'Customer (online)', created_at: iso(-420) }),
    ],
  },
];

// A seeded manual registration so the list and history are not empty
const registrations = [];
let nextRegistrationNumber = 1;
let nextCompanyId = 200;
let nextEntryNumber = 1;
const idempotentResults = new Map();

function registrationNo() {
  const number = String(nextRegistrationNumber).padStart(4, '0');
  nextRegistrationNumber += 1;
  return `MR-${number}`;
}

(function seed() {
  const company = companies.find((c) => c.id === 104);
  const previous = company.history[0];
  const registration = {
    id: 'seed-1', registration_no: registrationNo(), company_id: company.id, company_name: company.name,
    admin: { id: 104, name: 'Priya Nair', email: 'priya@acmetraders.example' },
    plan_id: 2, plan_name: 'Basic', licences: 3, duration_days: 365, list_price: 7788, amount: 7000, currency: 'INR',
    price_override_reason: 'Loyalty discount agreed with the customer', payment_method: 'bank_transfer',
    payment_reference: 'HDFC-UTR-482910376', payment_date: iso(-12).slice(0, 10), remarks: 'Renewal, paid by NEFT.',
    proof: { name: 'neft-receipt.png', type: 'image/png', size: 184320, sha256: '9f2c…e1a7', url: null },
    status: 'active', change_type: 'renewal', previous_plan_name: previous.plan_name,
    start_date: iso(-12), expiry_date: iso(353), document_limit: 30000, documents_used_snapshot: previous.documents_used,
    tokens_moved: 3, send_email: true, email_status: 'sent', created_by: ADMIN_NAME, created_at: iso(-12),
    cancelled_at: null, cancel_reason: null,
  };
  registrations.push(registration);
  previous.state = 'closed';
  previous.close_reason = 'Renewed';
  company.history.unshift(entry('h104c', {
    kind: 'manual', change_type: 'renewal', plan_name: 'Basic', previous_plan_name: 'Basic', start: registration.start_date,
    expiry: registration.expiry_date, licences: 3, document_limit: 30000, documents_used: 0, amount: 7000,
    method: 'bank_transfer', reference: registration.payment_reference, registration_id: registration.id,
    has_receipt: true, actor: ADMIN_NAME, created_at: registration.created_at,
  }));
}());

// ---- rules ----
const currentOf = (company) => company.history[0] || null;
const isExpired = (e) => e && (e.state === 'expired' || (e.state === 'active' && new Date(e.expiry) < new Date()));

export function subscriptionState(company) {
  const current = currentOf(company);
  if (!current) return { key: 'none', label: 'No subscription', current: null };
  const expired = isExpired(current);
  const trial = current.plan_name === 'Free Trial';
  if (trial) return { key: expired ? 'trial_expired' : 'trial_active', label: expired ? 'Free trial expired' : 'Free trial active', current };
  return { key: expired ? 'paid_expired' : 'paid_active', label: expired ? 'Paid plan expired' : 'Paid plan active', current };
}

// What a registration would do to the account; shown before saving and applied when saving.
// `summary` is a company search result (see companySummary) or null for a brand-new company.
export function describeChange(summary, plan, licences, { startDate = new Date(), expiryDate = null, plans = PLANS } = {}) {
  const price = (p, n) => (p ? p.price_per_licence * n : 0);
  const sub = summary ? summary.subscription : null;
  const stateKey = sub ? sub.state : 'none';
  const lines = [];
  let changeType = 'new';
  const startMs = startDate.getTime();
  let startsFrom = startMs;

  if (!summary) {
    lines.push('A new company account is created, already verified and active.');
    lines.push('A “set your password” email is sent to the administrator.');
  } else if (stateKey === 'trial_active') {
    changeType = 'trial_conversion';
    lines.push('The active free trial is closed as “Converted to paid”; its dates and limits stay in the history.');
  } else if (stateKey === 'trial_expired') {
    changeType = 'trial_conversion';
    lines.push('The expired free trial stays in the history unchanged.');
  } else if (stateKey === 'paid_active') {
    const oldPlan = plans.find((p) => p.name === sub.plan_name);
    const oldValue = price(oldPlan, sub.licences);
    const newValue = price(plan, licences);
    if (plan && oldPlan && plan.id === oldPlan.id && licences >= sub.licences) changeType = 'renewal';
    else changeType = newValue >= oldValue ? 'upgrade' : 'downgrade';
    if (changeType === 'renewal') {
      startsFrom = Math.max(startMs, new Date(sub.expiry).getTime());
      lines.push('Renewal: the days left on the current plan are added to the new period, so no paid days are lost.');
    }
    lines.push(`The current ${sub.plan_name} subscription is closed as “${CHANGE_TYPES[changeType]}”; its record stays in the history.`);
  } else if (stateKey === 'paid_expired') {
    changeType = 'renewal';
    lines.push('The expired subscription stays in the history; a new period starts today.');
  } else {
    lines.push('This company has no subscription yet; a new one is created.');
  }

  if (summary && sub && sub.plan_name && summary.tokens > 0 && licences) {
    const moved = Math.min(summary.tokens, licences);
    lines.push(`${moved} existing licence token${moved === 1 ? '' : 's'} move to the new subscription (no duplicates are created).`);
    lines.push(`Documents used so far (${(sub.documents_used || 0).toLocaleString('en-IN')}) are saved as a snapshot before the change.`);
  }
  // Default expiry: the plan's duration after the start (after the current expiry for a renewal of an active plan)
  const suggestedExpiry = new Date(startsFrom + (plan ? plan.duration_days : 0) * DAY);
  if (plan && licences) {
    const expiry = expiryDate || suggestedExpiry;
    lines.push(`New subscription: ${plan.name}, ${licences} licence${licences === 1 ? '' : 's'}, valid until ${expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.`);
  }
  return { changeType, lines, suggestedExpiry };
}

const normalizeReference = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

async function sha256Hex(file) {
  try {
    if (!window.crypto?.subtle) return null;
    const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return null;
  }
}

const companySummary = (company) => {
  const state = subscriptionState(company);
  const current = state.current;
  return {
    id: company.id,
    name: company.name,
    admins: clone(company.admins),
    tokens: company.tokens,
    subscription: {
      state: state.key,
      state_label: state.label,
      plan_name: current ? current.plan_name : null,
      licences: current ? current.licences : 0,
      start: current ? current.start : null,
      expiry: current ? current.expiry : null,
      documents_used: current ? current.documents_used : 0,
      document_limit: current ? current.document_limit : null,
    },
  };
};

// A payment made through the application, in the same shape as a registration but read only
function onlinePayment(entryId) {
  const company = companies.find((c) => c.history.some((h) => h.id === entryId));
  const e = company && company.history.find((h) => h.id === entryId);
  if (!e) throw failure(404, 'Payment not found');
  const owner = company.admins[0];
  return {
    id: `online-${e.id}`, source: 'online', registration_no: 'Online payment', company_id: company.id, company_name: company.name,
    admin: { id: owner.id, name: owner.name, email: owner.email }, plan_id: null, plan_name: e.plan_name, licences: e.licences,
    duration_days: Math.round((new Date(e.expiry) - new Date(e.start)) / DAY), list_price: e.amount, amount: e.amount,
    currency: 'INR', price_override_reason: null, payment_method: 'razorpay', payment_reference: e.reference,
    payment_date: String(e.created_at).slice(0, 10), remarks: '', proof: null, status: (e.state === 'active' && new Date(e.expiry) < new Date()) ? 'expired' : e.state,
    change_type: e.change_type, previous_plan_name: e.previous_plan_name, start_date: e.start, expiry_date: e.expiry,
    document_limit: e.document_limit, documents_used_snapshot: e.documents_used, tokens_moved: 0, send_email: null,
    created_by: e.actor || 'Customer (online)', created_at: e.created_at, cancelled_at: null, cancel_reason: null,
    can_cancel: false, cancel_blocked_reason: null,
  };
}

const stateOf = (e) => (e.state === 'active' && new Date(e.expiry) < new Date() ? 'expired' : e.state);

// One row per paid subscription of every company; trials are not paid
function paidRows() {
  const rows = [];
  companies.forEach((company) => {
    company.history.forEach((e) => {
      if (e.kind === 'trial') return;
      const registration = e.registration_id ? registrations.find((r) => r.id === e.registration_id) : null;
      const owner = company.admins[0];
      rows.push({
        id: registration ? registration.id : `online-${e.id}`,
        source: registration ? 'manual' : 'online',
        registration_no: registration ? registration.registration_no : null,
        company_id: company.id,
        company_name: company.name,
        admin: registration ? registration.admin : { id: owner.id, name: owner.name, email: owner.email },
        plan_name: e.plan_name,
        licences: e.licences,
        amount: e.amount,
        currency: 'INR',
        paid_via: registration ? registration.payment_method : 'online',
        payment_reference: e.reference,
        payment_date: registration ? registration.payment_date : String(e.created_at).slice(0, 10),
        status: registration && registration.status === 'cancelled' ? 'cancelled' : stateOf(e),
        change_type: e.change_type,
        created_at: e.created_at,
      });
    });
  });
  return rows;
}

export const demoApi = {
  async plans() {
    await delay(150);
    return clone(PLANS);
  },

  async searchCompanies(search) {
    await delay(200);
    const query = String(search || '').trim().toLowerCase();
    if (query.length < 2) return [];
    return companies
      .filter((c) => c.name.toLowerCase().includes(query)
        || c.admins.some((a) => a.email.toLowerCase().includes(query) || a.name.toLowerCase().includes(query)))
      .slice(0, 8)
      .map(companySummary);
  },

  async getCompany(id) {
    await delay(150);
    const company = companies.find((c) => c.id === Number(id));
    if (!company) throw failure(404, 'Company not found');
    return companySummary(company);
  },

  // Totals over every paid subscription (online and manual); cancelled ones are not counted
  async stats() {
    await delay(150);
    const rows = paidRows().filter((r) => r.status !== 'cancelled');
    const sum = (list) => list.reduce((total, r) => total + r.amount, 0);
    const online = rows.filter((r) => r.source === 'online');
    const manual = rows.filter((r) => r.source === 'manual');
    return {
      total_count: rows.length,
      total_amount: sum(rows),
      online_count: online.length,
      online_amount: sum(online),
      manual_count: manual.length,
      manual_amount: sum(manual),
    };
  },

  // Every paid subscription: payments made through the application and manual registrations, newest first
  async listPaidHistory({ page = 1, pageSize = 10, search = '', status = '', paidVia = '', changeType = '' } = {}) {
    await delay();
    const query = String(search).trim().toLowerCase();
    const matching = paidRows()
      .filter((r) => (!status || r.status === status)
        && (!paidVia || r.paid_via === paidVia)
        && (!changeType || r.change_type === changeType)
        && (!query || [r.company_name, r.admin.email, r.admin.name, r.payment_reference, r.registration_no]
          .some((value) => String(value || '').toLowerCase().includes(query))))
      .sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
    const items = matching.slice((page - 1) * pageSize, page * pageSize);
    return { items: clone(items), total: matching.length, page, page_size: pageSize };
  },

  async getRegistration(id) {
    await delay();
    if (String(id).startsWith('online-')) return onlinePayment(String(id).slice('online-'.length));
    const registration = registrations.find((r) => r.id === id);
    if (!registration) throw failure(404, 'Registration not found');
    const company = companies.find((c) => c.id === registration.company_id);
    // Cancelling is only safe while this is the company's newest subscription and nothing was signed on it
    const latest = company && company.history[0];
    const canCancel = registration.status === 'active' && !!latest && latest.registration_id === registration.id
      && (latest.documents_used || 0) === 0;
    return {
      ...clone(registration),
      proof: registration.proof ? { ...registration.proof } : null,   // keep the object URL as is
      can_cancel: canCancel,
      cancel_blocked_reason: canCancel || registration.status !== 'active' ? null
        : 'Documents were already signed on this subscription, or a newer subscription exists. Use the existing limit and validity tools to correct it.',
    };
  },

  // The real API fetches the private file; the demo keeps the object URL of the file uploaded this session
  async receiptUrl(registrationId) {
    await delay(100);
    const registration = registrations.find((r) => r.id === registrationId);
    return registration && registration.proof ? registration.proof.url : null;
  },

  async companyHistory(companyId) {
    await delay();
    const company = companies.find((c) => c.id === Number(companyId));
    if (!company) throw failure(404, 'Company not found');
    return clone(company.history).map((h) => ({
      ...h,
      state: h.state === 'active' && new Date(h.expiry) < new Date() ? 'expired' : h.state,
      receipt_url: h.registration_id ? (registrations.find((r) => r.id === h.registration_id)?.proof?.url || null) : null,
    }));
  },

  // The same idempotency key always gives the same registration, even for two submits at once
  async createRegistration(form) {
    const pending = idempotentResults.get(form.idempotency_key);
    if (pending) return clone(await pending);
    const work = manualRegistrationsApi.createOnce(form);
    idempotentResults.set(form.idempotency_key, work);
    try {
      return clone(await work);
    } catch (err) {
      idempotentResults.delete(form.idempotency_key);   // a failed attempt can be retried with the same key
      throw err;
    }
  },

  async createOnce(form) {
    await delay(500);

    const plan = PLANS.find((p) => p.id === Number(form.plan_id));
    if (!plan) throw failure(400, 'Choose a plan');
    const reference = normalizeReference(form.payment_reference);
    const duplicate = registrations.find((r) => r.status === 'active' && normalizeReference(r.payment_reference) === reference
      && r.payment_method === form.payment_method);
    if (duplicate) {
      throw failure(409, `This ${form.payment_method.replace('_', ' ')} reference is already recorded (${duplicate.registration_no}, ${duplicate.company_name})`);
    }

    let company;
    let owner;
    if (form.company_id) {
      company = companies.find((c) => c.id === Number(form.company_id));
      if (!company) throw failure(404, 'Company not found');
      owner = company.admins.find((a) => a.id === Number(form.owner_user_id)) || company.admins[0];
    } else {
      const { name, admin_name: adminName, email, phone } = form.new_company || {};
      if (companies.some((c) => c.name.trim().toLowerCase() === String(name).trim().toLowerCase())) {
        throw failure(409, 'An account with this company name already exists. Select it as an existing company instead.');
      }
      if (companies.some((c) => c.admins.some((a) => a.email.toLowerCase() === String(email).trim().toLowerCase()))) {
        throw failure(409, 'An account with this email already exists. Select its company as an existing company instead.');
      }
      nextCompanyId += 1;
      owner = { id: nextCompanyId, name: adminName.trim(), email: email.trim().toLowerCase(), phone: String(phone).trim() };
      company = { id: nextCompanyId, name: name.trim(), tokens: 0, admins: [owner], history: [] };
      companies.push(company);
    }

    const licences = Number(form.licences);
    if (!Number.isInteger(licences) || licences < plan.min_licences || licences > plan.max_licences) {
      throw failure(400, `${plan.name} allows ${plan.min_licences} to ${plan.max_licences} licences`);
    }
    let startDate = form.start_date ? new Date(`${form.start_date}T00:00:00`) : new Date();
    if (startDate.toDateString() === new Date().toDateString()) startDate = new Date();   // today means now
    if (Number.isNaN(startDate.getTime()) || startDate.getTime() > Date.now()) throw failure(400, 'The start date cannot be in the future');
    const change = describeChange(form.company_id ? companySummary(company) : null, plan, licences, { startDate });
    const expiryDate = form.expiry_date ? new Date(`${form.expiry_date}T23:59:59`) : change.suggestedExpiry;
    if (Number.isNaN(expiryDate.getTime()) || expiryDate <= startDate) throw failure(400, 'The expiry date must be after the start date');
    if (expiryDate.getTime() <= Date.now()) throw failure(400, 'The expiry date has already passed');
    const durationDays = Math.max(1, Math.floor((expiryDate.getTime() - startDate.getTime()) / DAY));   // whole days, as the form shows
    const previous = currentOf(company);
    const snapshot = previous ? previous.documents_used || 0 : 0;
    const tokensMoved = previous ? Math.min(company.tokens, licences) : 0;
    if (previous && previous.state === 'active') {
      previous.state = 'closed';
      previous.close_reason = {
        trial_conversion: 'Converted to paid', renewal: 'Renewed', upgrade: 'Upgraded', downgrade: 'Downgraded',
      }[change.changeType] || 'Closed';
    }

    const sha256 = form.proof ? await sha256Hex(form.proof) : null;
    const registration = {
      id: `mr-${Date.now()}-${nextEntryNumber}`, registration_no: registrationNo(), company_id: company.id,
      company_name: company.name, admin: { id: owner.id, name: owner.name, email: owner.email },
      plan_id: plan.id, plan_name: plan.name, licences, duration_days: durationDays,
      list_price: priceBreakdown(plan.price_per_licence, licences).total, amount: Number(form.amount), currency: 'INR',
      price_override_reason: form.price_override_reason || null, payment_method: form.payment_method,
      payment_reference: form.payment_reference.trim(), payment_date: form.payment_date, remarks: form.remarks || '',
      proof: form.proof ? {
        name: form.proof.name, type: form.proof.type, size: form.proof.size, sha256,
        url: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(form.proof) : null,
      } : null,
      status: 'active', change_type: change.changeType, previous_plan_name: previous ? previous.plan_name : null,
      start_date: startDate.toISOString(), expiry_date: expiryDate.toISOString(),
      document_limit: licences * DOCUMENTS_PER_LICENCE, documents_used_snapshot: snapshot, tokens_moved: tokensMoved,
      send_email: Boolean(form.send_email), email_status: form.send_email ? 'sent' : 'not_sent',
      created_by: ADMIN_NAME, created_at: new Date().toISOString(), cancelled_at: null, cancel_reason: null,
    };
    registrations.push(registration);
    company.history.unshift(entry(`h${company.id}-${nextEntryNumber}`, {
      kind: 'manual', change_type: change.changeType, plan_name: plan.name, previous_plan_name: registration.previous_plan_name,
      start: registration.start_date, expiry: registration.expiry_date, licences, document_limit: registration.document_limit,
      documents_used: 0, amount: registration.amount, method: registration.payment_method,
      reference: registration.payment_reference, registration_id: registration.id, has_receipt: Boolean(form.proof),
      actor: ADMIN_NAME, created_at: registration.created_at, snapshot_of_previous: snapshot, tokens_moved: tokensMoved,
    }));
    nextEntryNumber += 1;
    company.tokens = Math.max(company.tokens, tokensMoved);

    return { id: registration.id, registration_no: registration.registration_no };
  },

  async cancelRegistration(id, reason) {
    await delay(400);
    const registration = registrations.find((r) => r.id === id);
    if (!registration) throw failure(404, 'Registration not found');
    if (registration.status === 'cancelled') throw failure(409, 'This registration is already cancelled');
    const company = companies.find((c) => c.id === registration.company_id);
    const latest = company && company.history[0];
    if (!latest || latest.registration_id !== registration.id || (latest.documents_used || 0) > 0) {
      throw failure(409, 'This registration can no longer be cancelled: documents were signed on it or a newer subscription exists');
    }
    registration.status = 'cancelled';
    registration.cancelled_at = new Date().toISOString();
    registration.cancel_reason = reason;
    latest.state = 'cancelled';
    latest.close_reason = 'Cancelled by Product Admin';
    const restored = company.history.find((h) => h.state === 'closed');
    if (restored) {
      restored.state = new Date(restored.expiry) < new Date() ? 'expired' : 'active';
      restored.close_reason = null;
    }
    return { id, status: 'cancelled' };
  },
};

const useDemo = process.env.REACT_APP_MANUAL_REGISTRATIONS_DEMO === 'true' || process.env.NODE_ENV === 'test';

// What the screens use: the real API, or the demo store when asked for (and in tests)
export const manualRegistrationsApi = useDemo ? demoApi : realApi;
